<template>
  <div class="flex flex-col">
    <label for="image" class="block text-sm font-medium text-gray-700">
      <div class="flex w-full items-center">
        <div class="w-3/4">Cover image</div>
        <div class="w-1/4">
          <div class="flex justify-end">
            <InfoTooltip width="360">
              <template v-slot:info-header>Uploading a cover image</template>
              <template v-slot:info-content>
                <ol class="list-decimal ml-4 font-bold">
                  <li>Select a high-resolution image.</li>
                  <li>Crop it to a square shape.</li>
                  <li>Adjust the size and position to fit the frame.</li>
                  <li>Save and upload.</li>
                </ol>
              </template>
            </InfoTooltip>
          </div>
        </div>
      </div>
    </label>

    <!-- When there is no image -->
    <div v-if="!image">
      <div class="mt-4">
        <div class="">
          <div class="cursor-pointer">
            <div class="flex items-center justify-center w-full cursor-pointer group">
              <label
                class="
                  flex flex-col
                  w-full
                  h-30
                  md:h-44
                  border-2 border-dashed
                  hover:border-gray-400
                  cursor-pointer
                  rounded-md
                "
              >
                <div class="flex flex-col w-full h-full items-center justify-center pt-5">
                  <svg
                    width="33"
                    height="33"
                    viewBox="0 0 33 33"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1.83398 23.8334L10.2413 15.4261C11.6732 13.9942 13.9948 13.9942 15.4267 15.4261L23.834 23.8334M20.1673 20.1667L23.0746 17.2594C24.5065 15.8275 26.8281 15.8275 28.26 17.2594L31.1673 20.1667M20.1673 9.16671H20.1857M5.50065 31.1667H27.5006C29.5257 31.1667 31.1673 29.5251 31.1673 27.5V5.50004C31.1673 3.475 29.5257 1.83337 27.5006 1.83337H5.50065C3.47561 1.83337 1.83398 3.475 1.83398 5.50004V27.5C1.83398 29.5251 3.47561 31.1667 5.50065 31.1667Z"
                      stroke="#C4C4C4"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <p
                    class="
                      text-center
                      pt-4
                      text-sm
                      font-medium
                      text-gray-300
                      group-hover:text-gray-400
                    "
                  >
                    Upload image
                  </p>
                </div>
                <input
                  data-cy="session-image"
                  type="file"
                  accept=".png, .jpeg, .jpg"
                  class="opacity-0 pointer-events-none"
                  id="image"
                  @change="onImageChange"
                />
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="mt-4">
      <div
        class="
          w-full
          h-full
          relative
          flex
          border border-gray-200
          justify-between
          items-center
          rounded-md
        "
      >
        <img
          class="img-fluid w-full h-full object-contain rounded-md"
          :src="croppedImage || image"
        />
        <button
          v-if="!imageLoading"
          @click="removeImage"
          type="button"
          class="
            absolute
            -top-1.5
            -right-2.5
            border border-transparent
            rounded-full
            shadow-sm
            text-sm
            flex
            justify-center
            items-center
            font-medium
            text-white
            w-6
            h-6
            bg-vice-rose-400
            hover:bg-vice-rose-500
            focus:outline-none
            focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
          "
        >
          <XIcon class="w-4 h-4"></XIcon>
        </button>
      </div>
    </div>
    <jet-input-error :message="imageError || $page.props.errors.image" class="mt-2" />
  </div>
  <TransitionRoot as="div" :show="cropperVisible">
    <Dialog as="div" class="fixed z-20 inset-0 overflow-y-auto">
      <div class="flex items-end justify-center pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 translate-y-0 sm:scale-100"
          leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <div
            class="
              modal-container
              inline-block
              align-bottom
              bg-white
              rounded-lg
              px-4
              pt-5
              pb-4
              text-left
              overflow-hidden
              shadow-xl
              transform
              transition-all
              sm:my-8
              sm:align-middle
              sm:max-w-sm
              sm:w-full
              sm:p-10
            "
          >
            <cropper ref="cropper" class="cropper" :src="image" />
            <div class="flex gap-4 mt-6 justify-end">
              <ViceButton red @click="cropperVisible = false">Cancel</ViceButton>
              <ViceButton @click="cropImage">Crop Image</ViceButton>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import { XIcon } from '@heroicons/vue/solid';
import { Dialog, DialogOverlay, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { Cropper } from 'vue-advanced-cropper';
import JetInputError from '@/Jetstream/InputError.vue';
import 'vue-advanced-cropper/dist/style.css';
import ViceButton from '@/Components/Buttons/ViceButton.vue';
import InfoTooltip from '@/Components/Tooltips/InfoTooltip.vue';

export default {
  name: 'SessionImage',
  components: {
    Dialog,
    DialogOverlay,
    TransitionChild,
    TransitionRoot,
    JetInputError,
    XIcon,
    Cropper,
    ViceButton,
    InfoTooltip,
  },
  emits: ['imageChanged', 'imageRemoved'],
  data() {
    return {
      open: true,
      image: this.$page.props.session?.details.image_url,
      cropperVisible: false,
      cropper: null,
      croppedImage: null,
      fileName: '',
      imageError: null,
    };
  },
  methods: {
    async imageUrlToBlob(url) {
      const response = await fetch(url);

      return response.blob();
    },
    blobToFile(blob, fileName) {
      try {
        return new File([blob], fileName, { type: blob.type });
      } catch (e) {
        const file = new Blob([blob], { type: blob.type });
        file.name = fileName;
        return file;
      }
    },

    onImageChange(e) {
      const files = e.target.files || e.dataTransfer.files;

      if (!files.length) return;

      const file = files[0];

      if (file.size > 25 * 1024 * 1024) {
        this.imageError =
          'Sorry, but you file is too large to upload! The maximum file size is 25MB.';
        return;
      }

      this.imageError = null;
      this.fileName = file.name;

      this.createImage(file);
      this.cropperVisible = true;
    },
    async cropImage() {
      const canvas = this.$refs.cropper.getResult().canvas;

      const croppedImageUrl = canvas.toDataURL('image/jpeg', 0.85);

      const blob = await this.imageUrlToBlob(croppedImageUrl);
      const file = this.blobToFile(blob, this.fileName.replace(/\.[^.]+$/, '.jpg'));

      this.croppedImage = croppedImageUrl;
      this.createImage(file);
      this.$emit('imageChanged', [file]);

      this.cropperVisible = false;
    },
    createImage(file) {
      const reader = new FileReader();
      const self = this;

      reader.onload = (e) => {
        self.image = e.target.result;
      };
      reader.readAsDataURL(file);
    },
    removeImage() {
      this.image = null;
      this.croppedImage = null;
      this.$emit('imageRemoved');
    },
  },
};
</script>

<style>
.vue-advanced-cropper__background {
  background-color: white !important;
}
</style>
